import { ChangeLogAccumulator, type SelfSufficientChange } from "./change-log.model";
import { PreconditionFailedError } from "./errors";
import { InvalidInstanceError, InvalidInstanceTransitionError } from "./errors.model";
import type { FlowInstanceId } from "./flow-instance.model";
import type { VersionedCanonical } from "./lib/canonical";
import { toCanonicalRef } from "./lib/canonical";
import type { DomainId } from "./lib/domain-id";
import type { PatientFactSource } from "./lib/patient-facts";
import { assertAnswerFits } from "./lib/questionnaire-answer-fit";
import { reconcileScoreObservations, scoreObservationId } from "./lib/score-observations";
import type { MemberId } from "./member-identity";
import { asObservationId, Observation } from "./observation.model";
import type { PatientId } from "./patient.model";
import type { QuestionnaireAnswerFact, QuestionnaireDefinition } from "./questionnaire-definition.model";
import { type MaterializableDefinition, materializeDefinition } from "./spine.model";
import type { QuestionnaireAnswerValue, UnitValue } from "./value.model";

/** Stable QuestionnaireInstance identity, independent of its QuestionnaireResponse locator. */
export type QuestionnaireInstanceId = DomainId<"QuestionnaireInstanceId">;

export function asQuestionnaireInstanceId(value: string): QuestionnaireInstanceId {
  if (!value) throw new PreconditionFailedError("A questionnaire instance identity cannot be empty");
  return value as QuestionnaireInstanceId;
}

// ============================================================
// QuestionnaireInstance — one patient's answers to one pinned revision
//
// PINS, NEVER SNAPSHOTS. The instance holds `url|version` and no copy of the
// definition. Revisions are immutable, so the pin cannot change under it, and
// a definition is deactivated rather than deleted, so it stays retrievable.
// The cost is real and taken deliberately: EVERY COMMAND THAT VALIDATES NEEDS
// THE DEFINITION LOADED AND PASSED IN. `$validate` checks base-spec conformance
// only and never cross-checks a response's answers against its Questionnaire,
// so every rule below is ours and none of it can be pushed to the server.
//
// ANSWERS ARE FLAT. One level of repeating group — settled on the definition —
// means an answer's address is fully described by question identity plus, at
// most, which repetition it belongs to. There is no depth to recurse, so
// lookup is a map read, the two recursive collectors the old entity carried
// are gone, and structure stays in the definition where it belongs. The mapper
// rebuilds the nested FHIR shape on write and flattens on read.
//
// NO ATTRIBUTION FIELD. The portal is patient-scoped and passwordless, so the
// answerer is always the subject; a field restating `patient` on every record
// teaches a distinction nobody can observe. The typed-actor union across six
// FHIR resource types does not come back with a proxy workflow either — when
// one arrives it is an exception field, present only when someone other than
// the patient answered.
//
// NOT INCLUDED: an embedded definition snapshot, `author` / `source` /
// `answeredBy`, `encounter` (a questionnaire is completed on the patient's own
// time and an overlapping visit is incidental), or a separate assignment
// aggregate/snapshot. `assignedAt` is the one stable opening fact.
// ============================================================

/**
 * One vocabulary, shared with the Exam, because FHIR forces one required
 * `QuestionnaireResponse.status` field and the mapper would otherwise have to
 * invent a value either way. Only the state set is shared — the TRANSITIONS
 * are per-model, and a questionnaire's completion demands only that its
 * required questions are answered.
 *
 * `underAmendment` is distinct from `inProgress` on purpose: a reopened record
 * that someone walks away from should read as being corrected, not as never
 * finished.
 */
export type QuestionnaireInstanceStatus =
  | "inProgress"
  | "completed"
  | "underAmendment"
  | "amended"
  | "stopped"
  | "enteredInError";

/**
 * What a patient actually answered.
 *
 * DISTINCT FROM `QuestionnaireAnswerValue`, which is the DECLARATION — the
 * question's type and its constraints. This is the recorded value that
 * declaration admits, and the two are validated against each other on every
 * write. Kept beside the instance rather than in `value.model.ts` because
 * nothing else in the redesign records patient-supplied values yet; when the
 * order set needs recorded parameter values, the two should be reconciled into
 * one foundation type.
 *
 * A `Multiple`-cardinality choice produces SEVERAL entries sharing a
 * `questionId` and `occurrence` rather than one list-valued answer, so every
 * question has one shape whatever its cardinality.
 */
export type QuestionnaireAnswerContent =
  | { kind: "Quantity"; value: number; unit?: UnitValue }
  | { kind: "Integer"; value: number }
  | { kind: "Choice"; optionId?: MemberId; freeText?: string }
  | { kind: "Boolean"; value: boolean }
  | { kind: "Text"; value: string }
  | { kind: "Date"; value: string }
  | { kind: "Attachment"; url: string; contentType?: string; title?: string }
  | { kind: "Url"; value: string };

/** One answer at one address. Sparse: an absent entry means unanswered, never "declined". */
export interface QuestionnaireAnswer {
  questionId: MemberId;
  /** Which repetition, for a question inside a repeating group. Absent everywhere else. */
  occurrence?: number;
  value: QuestionnaireAnswerContent;
}

/**
 * The slice of a definition revision this instance validates against.
 *
 * A structural view rather than the `QuestionnaireDefinition` class, so the
 * instance depends on the FACTS it needs and not on the aggregate that happens
 * to hold them — the definition is loaded by the handler and flattened once,
 * and a fixture in a test supplies four fields instead of building a catalog.
 */
export interface AnswerableQuestion {
  readonly id: MemberId;
  readonly label: string;
  readonly value: QuestionnaireAnswerValue;
  /** The repeating group this question lives in. Absent when it sits directly in a section. */
  readonly repeatingGroupId?: MemberId;
}

export interface QuestionnaireDefinitionView {
  readonly url: string;
  readonly version: number;
  readonly questions: readonly AnswerableQuestion[];
}

/**
 * What completion needs and cannot work out for itself.
 *
 * Which questions are required, and which are currently disabled by their
 * conditions, are the DEFINITION's answers — `completionRequirements()` already
 * walks conditions correctly, respecting parent enablement. The instance keeps
 * taking that answer as an argument rather than learning to walk the definition
 * itself.
 */
export interface QuestionnaireCompletionRequirement {
  questionId: MemberId;
  /** Present only for a question inside a repeating group. */
  occurrence?: number;
}

export interface QuestionnaireCompletionFacts {
  requiredAnswers: readonly QuestionnaireCompletionRequirement[];
  /** Questions whose conditions no longer fire. Their answers are PRUNED here — see below. */
  disabledQuestionIds?: readonly MemberId[];
  /** Stamped as `completedAt`. Passed in because a model that reads the clock cannot be tested. */
  at: string;
}

/**
 * The flow run that assigned this instance, and the node it answers. Absent on
 * a hand assignment, so a form assigned outside a flow never moves one, even
 * when some flow routes that questionnaire.
 *
 * Not an answerer attribution (see NO ATTRIBUTION FIELD above): this says
 * which run put the form in front of the patient.
 */
export interface QuestionnaireFlowStep {
  flowInstanceId: FlowInstanceId;
  nodeId: MemberId;
}

export interface QuestionnaireInstanceFacts {
  id: QuestionnaireInstanceId;
  /** The revision this instance is an instance OF. Always pinned. */
  definition: VersionedCanonical;
  patientId: PatientId;
  /** When this patient-owned instance was assigned/opened. Immutable. */
  assignedAt: string;
  status: QuestionnaireInstanceStatus;
  /** When the instance was most recently completed or amended. */
  completedAt?: string;
  answers: QuestionnaireAnswer[];
  flowStep?: QuestionnaireFlowStep;
  amendment?: { baseline: readonly QuestionnaireAnswer[]; reason: string };
}

/** One recorded change, self-sufficient: values, never pointers at live state. */
type QuestionnaireInstanceChange = SelfSufficientChange &
  (
    | { kind: "AnswerRecorded"; questionId: MemberId; occurrence?: number; to: QuestionnaireAnswerContent }
    | { kind: "AnswerCleared"; questionId: MemberId; occurrence?: number; from: readonly QuestionnaireAnswerContent[] }
    | { kind: "AnswerPruned"; questionId: MemberId; occurrence?: number; from: readonly QuestionnaireAnswerContent[] }
  );

/** Statuses that accept answers. */
const OPEN: ReadonlySet<QuestionnaireInstanceStatus> = new Set<QuestionnaireInstanceStatus>([
  "inProgress",
  "underAmendment",
]);

/** Statuses a finished record can be reopened from. */
const FINISHED: ReadonlySet<QuestionnaireInstanceStatus> = new Set<QuestionnaireInstanceStatus>([
  "completed",
  "amended",
]);

/** Nothing moves out of these; they are where a record stops. */
const TERMINAL: ReadonlySet<QuestionnaireInstanceStatus> = new Set<QuestionnaireInstanceStatus>([
  "stopped",
  "enteredInError",
]);

export class QuestionnaireInstance extends ChangeLogAccumulator<QuestionnaireInstanceChange> {
  private state: QuestionnaireInstanceFacts;
  /**
   * The finished answer state captured at reopen. NOT read off the change log:
   * the log is provenance and nothing may branch on it. Comparing final state
   * to this baseline also catches edits that were subsequently reverted.
   */
  private pendingAmendmentReason?: string;

  private constructor(state: QuestionnaireInstanceFacts) {
    super();
    this.state = structuredClone(state);
    assertPinned(this.state.definition);
    if (!this.state.patientId) {
      throw new InvalidInstanceError("A questionnaire instance requires a patient");
    }
    if (!this.state.assignedAt) {
      throw new InvalidInstanceError("A questionnaire instance requires an assignment timestamp");
    }
  }

  /** A patient starting a form. Revision-pinned at creation and never repointed. */
  static start(input: {
    id: QuestionnaireInstanceId;
    definition: MaterializableDefinition;
    patientId: PatientId;
    assignedAt: string;
  }): QuestionnaireInstance {
    return QuestionnaireInstance.open(
      input.id,
      materializeDefinition(input.definition),
      input.patientId,
      input.assignedAt,
    );
  }

  /**
   * A flow run assigning the form at the revision the run pinned. A superseded
   * revision is allowed here and only here: the run fixed its forms when it
   * began, and revising a questionnaire must not change a run under way. An
   * inactive lineage still refuses, since that is a decision to stop using it.
   */
  static startForFlow(input: {
    id: QuestionnaireInstanceId;
    definition: VersionedCanonical & { availability: MaterializableDefinition["availability"] };
    patientId: PatientId;
    assignedAt: string;
    flowStep: QuestionnaireFlowStep;
  }): QuestionnaireInstance {
    if (input.definition.availability !== "Active") {
      throw new PreconditionFailedError(`'${input.definition.url}' is inactive and cannot produce new work`);
    }
    return QuestionnaireInstance.open(
      input.id,
      { url: input.definition.url, version: input.definition.version },
      input.patientId,
      input.assignedAt,
      { ...input.flowStep },
    );
  }

  private static open(
    id: QuestionnaireInstanceId,
    definition: VersionedCanonical,
    patientId: PatientId,
    assignedAt: string,
    flowStep?: QuestionnaireFlowStep,
  ): QuestionnaireInstance {
    return new QuestionnaireInstance({
      id: asQuestionnaireInstanceId(id),
      definition,
      patientId,
      assignedAt,
      status: "inProgress",
      answers: [],
      ...(flowStep ? { flowStep } : {}),
    });
  }

  /** Rehydrate persisted state. The mapper's door in; it bypasses the commands by design. */
  static restore(facts: QuestionnaireInstanceFacts): QuestionnaireInstance {
    return new QuestionnaireInstance(facts);
  }

  get id(): QuestionnaireInstanceId {
    return this.state.id;
  }

  get patientId(): PatientId {
    return this.state.patientId;
  }

  get status(): QuestionnaireInstanceStatus {
    return this.state.status;
  }

  get assignedAt(): string {
    return this.state.assignedAt;
  }

  get definition(): VersionedCanonical {
    return { ...this.state.definition };
  }

  /** The pin as FHIR stores it, `url|version`. */
  get definitionRef(): string {
    return toCanonicalRef(this.state.definition) as string;
  }

  /** Use the current birth date and this instance's stable assignment instant. */
  patientFacts(birthDate: string | undefined): PatientFactSource {
    if (!birthDate)
      throw new InvalidInstanceError("Patient birth date is required to evaluate questionnaire conditions");
    return { birthDate, assignedAt: this.state.assignedAt };
  }

  get completedAt(): string | undefined {
    return this.state.completedAt;
  }

  get flowStep(): QuestionnaireFlowStep | undefined {
    return this.state.flowStep && structuredClone(this.state.flowStep);
  }

  /**
   * The flow step this record advances from, which is only ever its FIRST
   * completion. `completed` is reached once: finishing an amendment lands on
   * `amended`, so a correction never re-routes, whatever the first completion
   * routed to.
   */
  get flowStepToAdvance(): QuestionnaireFlowStep | undefined {
    return this.state.status === "completed" ? this.flowStep : undefined;
  }

  get isOpen(): boolean {
    return OPEN.has(this.state.status);
  }

  get isComplete(): boolean {
    return FINISHED.has(this.state.status);
  }

  scoreObservationId(scoreId: MemberId) {
    return scoreObservationId("QuestionnaireScore", this.id, scoreId);
  }

  /** Reconcile derived score observations against this committed response. */
  reconcileScores(
    definition: QuestionnaireDefinition,
    existing: readonly Observation[],
    ids: Readonly<Record<string, string>>,
  ): Observation[] {
    const scores = existing.filter((observation) => observation.scoreId !== undefined);
    if (this.state.status === "enteredInError") {
      for (const score of scores) if (!score.isVoid) score.retract();
      return [];
    }
    if (this.state.status !== "completed" && this.state.status !== "amended") return [];
    if (definition.url !== this.state.definition.url || String(definition.version) !== this.state.definition.version) {
      throw new InvalidInstanceError(
        `This instance answers ${this.definitionRef}, not ${definition.url}|${definition.version}`,
      );
    }
    const at = this.state.completedAt;
    if (!at) throw new InvalidInstanceError("A completed questionnaire requires a completion time");
    return reconcileScoreObservations(
      definition,
      definition.computeScores(this.state.answers),
      scores,
      at,
      (result, label) =>
        Observation.questionnaireScore({
          id: asObservationId(ids[result.scoreId] ?? ""),
          instanceId: this.id,
          patientId: this.patientId,
          scoreId: result.scoreId,
          label,
          value: result.value,
          band: result.band,
          at,
        }),
    );
  }

  get answers(): readonly QuestionnaireAnswer[] {
    return structuredClone(this.state.answers);
  }

  /** The answers as a condition tests them: one comparable value per answer. */
  get conditionFacts(): QuestionnaireAnswerFact[] {
    return this.state.answers.map((answer) => ({
      questionId: answer.questionId,
      occurrence: answer.occurrence,
      value:
        answer.value.kind === "Choice"
          ? (answer.value.optionId ?? answer.value.freeText)
          : answer.value.kind === "Attachment"
            ? answer.value.url
            : answer.value.value,
    }));
  }

  /** Every answer at one address, which is one entry unless the question is `Multiple`. */
  answersAt(questionId: MemberId, occurrence?: number): readonly QuestionnaireAnswerContent[] {
    return structuredClone(
      this.state.answers.filter((answer) => sameAddress(answer, questionId, occurrence)).map((answer) => answer.value),
    );
  }

  snapshot(): QuestionnaireInstanceFacts {
    return this.facts();
  }

  facts(): QuestionnaireInstanceFacts {
    return structuredClone(this.state);
  }

  /**
   * Write ONE answer.
   *
   * Wholesale replace is deleted. It has a real failure mode once forms save
   * incrementally: a client holding a stale copy submits its version and
   * silently erases answers it never knew about. Per-answer, two sources can
   * only collide on the same question. It also makes the amendment log
   * meaningful — "which answers changed" is knowable from the commands, where
   * under wholesale replace it is a tree diff or nothing.
   *
   * Cost: one round trip per answer instead of per form, debounced in the UI.
   */
  recordAnswer(
    definition: QuestionnaireDefinitionView,
    questionId: MemberId,
    occurrence: number | undefined,
    value: QuestionnaireAnswerContent,
  ): void {
    this.assertOpen("answer");
    const question = this.question(definition, questionId);
    this.assertOccurrence(question, occurrence);
    assertAnswerFits(question, value, this.state.id);

    const cardinality = question.value.kind === "Choice" ? question.value.cardinality : "Single";
    if (cardinality === "Multiple") {
      // A second selection on the same question is an ADDITIONAL entry, not a
      // replacement — that is what makes `Multiple` one shape with `Single`.
      // Re-recording a selection the patient already made is a no-op, so it
      // produces neither a duplicate entry nor a log line.
      const already = this.state.answers.some(
        (answer) => sameAddress(answer, questionId, occurrence) && sameContent(answer.value, value),
      );
      if (already) return;
      this.state.answers.push({ questionId, occurrence, value });
    } else {
      this.state.answers = this.state.answers.filter((answer) => !sameAddress(answer, questionId, occurrence));
      this.state.answers.push({ questionId, occurrence, value });
    }
    this.noteChange({ kind: "AnswerRecorded", questionId, occurrence, to: structuredClone(value) });
  }

  /** Erase one address. Absence is unanswered, so this genuinely removes rather than writing a blank. */
  clearAnswer(questionId: MemberId, occurrence?: number): void {
    this.assertOpen("clear an answer on");
    const removed = this.state.answers.filter((answer) => sameAddress(answer, questionId, occurrence));
    if (removed.length === 0) return;
    this.state.answers = this.state.answers.filter((answer) => !sameAddress(answer, questionId, occurrence));
    this.noteChange({
      kind: "AnswerCleared",
      questionId,
      occurrence,
      from: removed.map((answer) => structuredClone(answer.value)),
    });
  }

  /**
   * Finish the record — `completed` from `inProgress`, `amended` from
   * `underAmendment`.
   *
   * CONDITIONAL ANSWERS: KEEP, THEN PRUNE. While in progress, answers to
   * questions whose conditions stopped firing STAY — deleting a patient's work
   * because they toggled a checkbox is destructive and unrecoverable, and
   * toggling back is common; disabled answers simply stop counting toward
   * completeness. At completion they are pruned, so the finished record holds
   * only answers to questions that were actually asked.
   *
   * The pruning is the DOMAIN's, not the mapper's. If the mapper dropped them
   * on write, domain state and stored record would disagree and a reload would
   * silently lose answers the entity believed it had.
   *
   * Cost: amending later and re-enabling a group means those answers are gone,
   * and the patient re-answers.
   */
  complete(facts: QuestionnaireCompletionFacts): void {
    if (!OPEN.has(this.state.status)) {
      throw new InvalidInstanceTransitionError(`Cannot complete a '${this.state.status}' questionnaire`, {
        instanceId: this.state.id,
        status: this.state.status,
      });
    }

    for (const questionId of facts.disabledQuestionIds ?? []) {
      const pruned = this.state.answers.filter((answer) => answer.questionId === questionId);
      if (pruned.length === 0) continue;
      this.state.answers = this.state.answers.filter((answer) => answer.questionId !== questionId);
      this.noteChange({
        kind: "AnswerPruned",
        questionId,
        from: pruned.map((answer) => structuredClone(answer.value)),
      });
    }

    const missing = facts.requiredAnswers.filter(
      (required) => !this.state.answers.some((answer) => sameAddress(answer, required.questionId, required.occurrence)),
    );
    if (missing.length > 0) {
      const addresses = missing.map(({ questionId, occurrence }) =>
        occurrence === undefined ? questionId : `${questionId}[${occurrence}]`,
      );
      throw new InvalidInstanceError(`Required questions are unanswered: ${addresses.join(", ")}`, {
        instanceId: this.state.id,
        definitionUrl: this.state.definition.url,
      });
    }

    if (this.state.status === "underAmendment") {
      // What must not be lost from today's `amend`: it refuses a semantically
      // empty amendment by comparing before and after, so the log does not fill
      // with entries recording nothing.
      if (!this.state.amendment || answersEqual(this.state.answers, this.state.amendment.baseline)) {
        throw new InvalidInstanceTransitionError("An amendment that changes nothing cannot be completed", {
          instanceId: this.state.id,
          status: this.state.status,
        });
      }
      this.state.status = "amended";
      this.pendingAmendmentReason = this.state.amendment.reason;
      this.state.amendment = undefined;
    } else {
      this.state.status = "completed";
    }
    this.state.completedAt = facts.at;
  }

  /**
   * AMENDMENT IS A MODE, NOT A METHOD. `reopen` moves a finished record to
   * `underAmendment`, the ordinary commands apply, and `complete` moves it to
   * `amended` and hands the log to the session. The deleted alternative —
   * `amend(reason, changes[])` with a variant per command — was the command
   * surface duplicated as data, and it drifted the moment a command was added
   * and its variant forgotten.
   *
   * The reason rides on the log entry rather than on a field: a single
   * `amendmentReason` overwritten on each amendment loses the first one.
   */
  reopen(reason: string): void {
    if (!FINISHED.has(this.state.status)) {
      throw new InvalidInstanceTransitionError(
        `Only a finished questionnaire can be reopened, not '${this.state.status}'`,
        {
          instanceId: this.state.id,
          status: this.state.status,
        },
      );
    }
    if (!reason.trim()) {
      throw new InvalidInstanceError("An amendment requires a reason", { instanceId: this.state.id });
    }
    this.state.status = "underAmendment";
    this.state.amendment = { baseline: structuredClone(this.state.answers), reason };
    this.pendingAmendmentReason = reason;
    // `completedAt` deliberately stays: reopening does not erase when the
    // patient last filed the response.
  }

  override drainChangeLog(context: Parameters<ChangeLogAccumulator<QuestionnaireInstanceChange>["drainChangeLog"]>[0]) {
    const entries = super.drainChangeLog({
      ...context,
      reason: context.reason ?? this.state.amendment?.reason ?? this.pendingAmendmentReason,
    });
    this.pendingAmendmentReason = undefined;
    return entries;
  }

  /**
   * Deliberate abandonment of work that was never finished — the patient
   * stopped filling the form in and it is not coming back.
   *
   * KEPT DISTINCT FROM `retract`, which the open question asked about: the two
   * land on different statuses, `stopped` and `enteredInError`, and collapsing
   * them would make one of a required FHIR value set's codes unreachable. They
   * also differ in what is true afterwards — a stopped record's answers happened,
   * an entered-in-error record's did not.
   */
  stop(): void {
    this.assertOpen("stop");
    this.state.status = "stopped";
    this.state.amendment = undefined;
  }

  /**
   * The record should never have existed. Allowed from a FINISHED record too,
   * which is the main case: a form filled in against the wrong patient is
   * discovered after it was completed.
   */
  retract(): void {
    if (TERMINAL.has(this.state.status)) {
      throw new InvalidInstanceTransitionError(`Cannot retract a '${this.state.status}' questionnaire`, {
        instanceId: this.state.id,
        status: this.state.status,
      });
    }
    this.state.status = "enteredInError";
    this.state.amendment = undefined;
  }

  private question(definition: QuestionnaireDefinitionView, questionId: MemberId): AnswerableQuestion {
    // The definition handed in must be the revision this instance pins.
    // Validating against a neighbouring revision would accept answers the
    // stored record cannot be read back against — the old entity spent a
    // constructor guard on the same hazard, checking its snapshot against its
    // provenance.
    if (definition.url !== this.state.definition.url || String(definition.version) !== this.state.definition.version) {
      throw new InvalidInstanceError(
        `This instance answers ${this.definitionRef}, not ${definition.url}|${definition.version}`,
        { instanceId: this.state.id, definitionUrl: definition.url, definitionVersion: definition.version },
      );
    }
    const question = definition.questions.find((candidate) => candidate.id === questionId);
    if (!question) {
      throw new InvalidInstanceError(`No question '${questionId}' in ${this.definitionRef}`, {
        instanceId: this.state.id,
        definitionUrl: definition.url,
        memberId: questionId,
      });
    }
    return question;
  }

  private assertOccurrence(question: AnswerableQuestion, occurrence: number | undefined): void {
    const repeating = question.repeatingGroupId !== undefined;
    if (repeating && (occurrence === undefined || !Number.isInteger(occurrence) || occurrence < 0)) {
      throw new InvalidInstanceError(
        `'${question.label}' is inside a repeating group, so an answer must say which occurrence it belongs to`,
        { instanceId: this.state.id, memberId: question.id },
      );
    }
    if (!repeating && occurrence !== undefined) {
      throw new InvalidInstanceError(`'${question.label}' is not inside a repeating group and has no occurrences`, {
        instanceId: this.state.id,
        memberId: question.id,
      });
    }
  }

  private assertOpen(action: string): void {
    if (!OPEN.has(this.state.status)) {
      throw new InvalidInstanceTransitionError(`Cannot ${action} a '${this.state.status}' questionnaire`, {
        instanceId: this.state.id,
        status: this.state.status,
      });
    }
  }

  /**
   * Log only what an amendment did. A change log entry per answer written
   * during ordinary filling would put a `Basic` on every keystroke's worth of
   * autosave — the log exists to record corrections to a finished record.
   */
  private noteChange(change: QuestionnaireInstanceChange): void {
    if (this.state.status !== "underAmendment") return;
    this.recordChange(change);
  }
}

function sameAddress(answer: QuestionnaireAnswer, questionId: MemberId, occurrence: number | undefined): boolean {
  return answer.questionId === questionId && answer.occurrence === occurrence;
}

function sameContent(left: QuestionnaireAnswerContent, right: QuestionnaireAnswerContent): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function answersEqual(left: readonly QuestionnaireAnswer[], right: readonly QuestionnaireAnswer[]): boolean {
  const key = (answer: QuestionnaireAnswer) =>
    `${answer.questionId}\0${answer.occurrence ?? ""}\0${JSON.stringify(answer.value)}`;
  return (
    JSON.stringify([...left].sort((a, b) => key(a).localeCompare(key(b)))) ===
    JSON.stringify([...right].sort((a, b) => key(a).localeCompare(key(b))))
  );
}

function assertPinned(definition: VersionedCanonical): void {
  if (!definition?.url?.trim() || !definition?.version?.trim()) {
    throw new InvalidInstanceError("A questionnaire instance must pin a definition revision as url|version", {
      definitionUrl: definition?.url,
    });
  }
}
